import { CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import { Pair } from '@kodiak-finance/v2-sdk'
import CurrencyLogo from 'components/CurrencyLogo'
import Loader from 'components/Loader'
import { ResponsiveButtonPrimary } from 'components/PositionList/styled'
import { MouseoverTooltip } from 'components/Tooltip'
import { useConnection } from 'hooks/useConnection'
import { useCurrencyId } from 'hooks/useCurrencyId'
import { useV2Pair } from 'hooks/useV2Pairs'
import JSBI from 'jsbi'
import { useContext } from 'react'
import React from 'react'
import { ThemeContext } from 'styled-components'
import { StyledInternalLink, ThemedText } from 'theme'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import { BIG_INT_ZERO } from '../../constants/misc'
import { useTokenBalance } from '../../state/connection/hooks'
import { unwrappedToken } from '../../utils/unwrappedToken'
import DoubleCurrencyLogo from '../DoubleLogo'
import * as S from '../PositionList/styled'
import { Dots } from '../swap/styleds'
import * as ItemS from './styled'

interface V2PositionListItemProps {
  pair: Pair
  showUnwrapped?: boolean
  totalSupply?: CurrencyAmount<Token>
  stakedBalance?: CurrencyAmount<Token> // optional balance to indicate that liquidity is deposited in mining pool
  valueUSD?: number
  userPoolBalance?: CurrencyAmount<Token>
  row: number
}

function V2PositionListItem({
  pair,
  stakedBalance,
  totalSupply: totalPoolTokens,
  valueUSD,
  userPoolBalance: providedUserPoolBalance,
  row,
}: V2PositionListItemProps) {
  const { account } = useConnection()
  const theme = useContext(ThemeContext)

  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)
  const currency0Id = useCurrencyId(currency0)
  const currency1Id = useCurrencyId(currency1)
  useV2Pair(currency0, currency1)

  const defaultPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const userPoolBalance = providedUserPoolBalance || defaultPoolBalance

  // const totalPoolTokens = useTotalSupply(pair.liquidityToken)
  // if staked balance balance provided, add to standard liquidity amount
  const totalUserBalance = stakedBalance ? userPoolBalance?.add(stakedBalance) : userPoolBalance

  const poolTokenPercentage =
    !!totalUserBalance &&
    !!totalPoolTokens &&
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, totalUserBalance.quotient)
      ? new Percent(totalUserBalance.quotient, totalPoolTokens.quotient)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!totalUserBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, totalUserBalance.quotient)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, totalUserBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, totalUserBalance, false),
        ]
      : [undefined, undefined]

  const formattedValue = valueUSD ? formatDollarAmount(valueUSD) : '-'

  return (
    <ItemS.V2ListItemFlex>
      <S.Td>
        <ItemS.PrimaryPositionIdData>
          <S.FlexRowCenter>
            <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={32} margin />
            <S.FlexRowCenter>
              <ItemS.PairText>
                {!currency0 || !currency1 ? (
                  <Dots>
                    <>Loading</>
                  </Dots>
                ) : (
                  `${currency0.symbol}/${currency1.symbol}`
                )}
              </ItemS.PairText>
            </S.FlexRowCenter>
          </S.FlexRowCenter>
        </ItemS.PrimaryPositionIdData>
      </S.Td>

      <S.Td>
        <MouseoverTooltip
          // disableHover={!tooltip}
          text={
            <S.FlexCol>
              <S.FlexRowCenter style={{ justifyContent: 'space-between' }}>
                {token0Deposited ? (
                  <>
                    <S.FlexRowCenter>
                      <CurrencyLogo size="20px" style={{ marginRight: '8px' }} currency={currency0} />
                      <ThemedText.DeprecatedBody color={theme.textSecondary} fontSize="14px" fontWeight={400}>
                        {currency0.symbol}
                      </ThemedText.DeprecatedBody>
                    </S.FlexRowCenter>
                    <ThemedText.DeprecatedBody color={theme.textPrimary} fontSize="14px" fontWeight={500}>
                      {token0Deposited?.toSignificant(6)}
                    </ThemedText.DeprecatedBody>
                  </>
                ) : (
                  '-'
                )}
              </S.FlexRowCenter>

              <S.FlexRowCenter style={{ justifyContent: 'space-between' }}>
                {token1Deposited ? (
                  <>
                    <S.FlexRowCenter>
                      <CurrencyLogo size="20px" style={{ marginRight: '8px' }} currency={currency1} />
                      <ThemedText.DeprecatedBody color={theme.textSecondary} fontSize="14px" fontWeight={400}>
                        {currency1.symbol}
                      </ThemedText.DeprecatedBody>
                    </S.FlexRowCenter>
                    <ThemedText.DeprecatedBody color={theme.textPrimary} fontSize="14px" fontWeight={500}>
                      {token1Deposited?.toSignificant(6)}
                    </ThemedText.DeprecatedBody>
                  </>
                ) : (
                  '-'
                )}
              </S.FlexRowCenter>

              <S.FlexRowCenter>
                <ThemedText.DeprecatedBody color={theme.textPrimary} fontSize="14px" fontWeight={400}>
                  <>Pool tokens:</>
                </ThemedText.DeprecatedBody>
                <ThemedText.DeprecatedBody color={theme.textPrimary} fontSize="14px" fontWeight={400}>
                  {userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}
                </ThemedText.DeprecatedBody>
              </S.FlexRowCenter>
            </S.FlexCol>
          }
        >
          <ThemedText.DeprecatedLargeHeader color={theme.textPrimary} fontSize="14px" fontWeight={400}>
            {formattedValue}
          </ThemedText.DeprecatedLargeHeader>
        </MouseoverTooltip>
      </S.Td>

      <S.Td>
        <ThemedText.DeprecatedLargeHeader color={theme.textPrimary} fontSize="14px" fontWeight={400}>
          {poolTokenPercentage ? (
            <>{poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)} %</>
          ) : (
            <Loader />
          )}
        </ThemedText.DeprecatedLargeHeader>
      </S.Td>

      <S.Td>
        {userPoolBalance && JSBI.greaterThan(userPoolBalance.quotient, BIG_INT_ZERO) && (
          <ItemS.ManageButtonFlex>
            <ResponsiveButtonPrimary
              data-cy="add-v2-button"
              id="add-v2-button"
              as={StyledInternalLink}
              to={`/liquidity/v2/add/${currency0Id}/${currency1Id}`}
            >
              +
            </ResponsiveButtonPrimary>
            <ResponsiveButtonPrimary
              data-cy="remove-v2-button"
              id="remove-v2-button"
              as={StyledInternalLink}
              to={`/liquidity/v2/remove/${currency0Id}/${currency1Id}`}
            >
              -
            </ResponsiveButtonPrimary>
          </ItemS.ManageButtonFlex>
        )}
      </S.Td>
    </ItemS.V2ListItemFlex>
  )
}

export default React.memo(V2PositionListItem, (prevProps, nextProps) => {
  if (prevProps === nextProps) return true

  if (prevProps.row !== nextProps.row) return false

  const prevPos = prevProps.pair
  const nextPos = nextProps.pair

  if (prevPos === nextPos) return true

  const criticalPropsEqual =
    prevPos.chainId.toString() === nextPos.chainId.toString() &&
    prevPos.reserve0.toExact() === nextPos.reserve0.toExact() &&
    prevPos.reserve1.toExact() === nextPos.reserve1.toExact() &&
    prevPos.token0.address.toLowerCase() === nextPos.token0.address.toLowerCase() &&
    prevPos.token1.address.toLowerCase() === nextPos.token1.address.toLowerCase() &&
    prevPos.token0Price.toFixed(8) === nextPos.token0Price.toFixed(8)

  if (!criticalPropsEqual) return false

  return true
})
